import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchAgents } from '@/entities/agent';
import { fetchEngagement, fetchEngagementBoard, fetchTrajectories, } from '@/entities/engagement';
import { advanceEngagementAction, cancelEngagementAction, } from '@/features/advance-engagement';
import { useAgentScope } from '@/features/agent-scope';
import { createEngagementAction } from '@/features/create-engagement';
import { updateTrajectoryStagesAction } from '@/features/manage-trajectory-stages';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, Sheet, Skeleton, } from '@/shared/ui';
import { AgentScopeToggle } from '@/widgets/agent-scope-toggle';
import { CreateEngagementForm } from './create-engagement-form';
import { EngagementKanban } from './engagement-kanban';
import { StageToolbar } from './stage-toolbar';
function mapActionError(err, kind) {
    if (!(err instanceof ApiError)) {
        return uiCopy.actionFailed;
    }
    if (err.status === 409) {
        return kind === 'trajectory'
            ? 'Программа с таким названием уже есть.'
            : 'Этот агент уже в этой программе.';
    }
    return err.message || uiCopy.actionFailed;
}
const DEFAULT_METRICS = [
    { key: 'fullName', label: 'ФИО', order: 0 },
    { key: 'status', label: 'Статус', order: 1 },
    { key: 'visitedProjectCount', label: 'Посетил проектов', order: 2 },
];
function moveCardOptimistically(columns, card, targetStageCode) {
    return columns.map((column) => {
        if (column.stageCode === card.stageCode) {
            return {
                ...column,
                engagements: column.engagements.filter((item) => item.engagementId !== card.engagementId),
            };
        }
        if (column.stageCode === targetStageCode) {
            return {
                ...column,
                engagements: [
                    ...column.engagements,
                    { ...card, stageCode: targetStageCode },
                ],
            };
        }
        return column;
    });
}
export function EngagementBoard() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { scope } = useAgentScope();
    const [trajectories, setTrajectories] = useState([]);
    const [title, setTitle] = useState('Воронки');
    const [trajectoryCode, setTrajectoryCode] = useState(null);
    const [placementMode, setPlacementMode] = useState('MANUAL');
    const [metrics, setMetrics] = useState(DEFAULT_METRICS);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStage, setSelectedStage] = useState(null);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [busy, setBusy] = useState(false);
    const [sheet, setSheet] = useState(null);
    const [agents, setAgents] = useState([]);
    const [agentsLoading, setAgentsLoading] = useState(false);
    const [occupiedAgentIds, setOccupiedAgentIds] = useState(() => new Set());
    const [boardQuery, setBoardQuery] = useState('');
    const [editingStages, setEditingStages] = useState(false);
    const trajectoryCodeRef = useRef(null);
    const columnsSnapshotRef = useRef([]);
    const isComputed = placementMode === 'COMPUTED';
    const load = useCallback(async (code) => {
        setLoading(true);
        setError(null);
        try {
            const list = await fetchTrajectories();
            setTrajectories(list.items);
            const queryCode = searchParams.get('trajectory');
            const nextCode = code ??
                (queryCode && list.items.some((item) => item.code === queryCode)
                    ? queryCode
                    : null) ??
                list.items.find((item) => item.code === 'BT_NOTIFICATIONS')?.code ??
                list.items.find((item) => item.code === 'BUS_TOURS')?.code ??
                list.items[0]?.code ??
                null;
            if (!nextCode) {
                setTrajectoryCode(null);
                setTitle('Воронки');
                setColumns([]);
                setSelectedStage(null);
                setPlacementMode('MANUAL');
                setMetrics(DEFAULT_METRICS);
                return;
            }
            const data = await fetchEngagementBoard(nextCode, scope);
            const switched = trajectoryCodeRef.current !== null &&
                trajectoryCodeRef.current !== data.trajectoryCode;
            setTitle(data.title);
            setTrajectoryCode(data.trajectoryCode);
            trajectoryCodeRef.current = data.trajectoryCode;
            setPlacementMode(data.placementMode ?? 'MANUAL');
            setMetrics(data.metrics && data.metrics.length > 0
                ? data.metrics
                : DEFAULT_METRICS);
            setColumns(data.columns);
            setSelectedStage((current) => {
                if (!switched &&
                    current &&
                    data.columns.some((column) => column.stageCode === current)) {
                    return current;
                }
                return data.columns[0]?.stageCode ?? null;
            });
        }
        catch (err) {
            const message = err instanceof ApiError ? err.message : uiCopy.loadFailed;
            setError(message);
        }
        finally {
            setLoading(false);
        }
    }, [scope, searchParams]);
    useEffect(() => {
        void load();
    }, [load]);
    useEffect(() => {
        setAgents([]);
        setOccupiedAgentIds(new Set());
    }, [scope, trajectoryCode]);
    useEffect(() => {
        setBoardQuery('');
        setEditingStages(false);
    }, [trajectoryCode]);
    useEffect(() => {
        if (!selected || selected.engagementId.startsWith('computed:')) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        void fetchEngagement(selected.engagementId)
            .then((data) => {
            if (!cancelled) {
                setDetail(data);
            }
        })
            .catch(() => {
            if (!cancelled) {
                setDetail(null);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [selected]);
    const busyAgentIds = useMemo(() => {
        const ids = new Set(occupiedAgentIds);
        for (const column of columns) {
            for (const item of column.engagements) {
                ids.add(item.agentId);
            }
        }
        return ids;
    }, [columns, occupiedAgentIds]);
    const selectedColumn = columns.find((column) => column.stageCode === selected?.stageCode) ??
        columns.find((column) => column.stageCode === selectedStage) ??
        columns[0];
    async function runAction(action, kind = 'agent') {
        setBusy(true);
        setError(null);
        try {
            await action();
            setSelected(null);
            setSheet(null);
        }
        catch (err) {
            setError(mapActionError(err, kind));
        }
        finally {
            setBusy(false);
        }
    }
    async function openCreateAgent() {
        setSheet('create-agent');
        setAgentsLoading(true);
        try {
            const [data, occupied] = await Promise.all([
                fetchAgents('all'),
                trajectoryCode
                    ? fetchEngagementBoard(trajectoryCode, 'all')
                    : Promise.resolve(null),
            ]);
            setAgents(data.items);
            if (occupied) {
                const ids = new Set();
                for (const column of occupied.columns) {
                    for (const item of column.engagements) {
                        ids.add(item.agentId);
                    }
                }
                setOccupiedAgentIds(ids);
            }
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
        }
        finally {
            setAgentsLoading(false);
        }
    }
    async function persistStages(stages) {
        if (!trajectoryCode || isComputed) {
            return;
        }
        await updateTrajectoryStagesAction(trajectoryCode, stages);
        await load(trajectoryCode);
    }
    async function moveStage(index, direction) {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= columns.length) {
            return;
        }
        const next = columns.map((column) => ({
            code: column.stageCode,
            title: column.title,
        }));
        const [item] = next.splice(index, 1);
        next.splice(nextIndex, 0, item);
        setBusy(true);
        setError(null);
        try {
            await persistStages(next);
        }
        catch (err) {
            setError(mapActionError(err, 'trajectory'));
            throw err;
        }
        finally {
            setBusy(false);
        }
    }
    async function handleAdvanceDrop(card, targetStageCode) {
        columnsSnapshotRef.current = columns;
        setColumns(moveCardOptimistically(columns, card, targetStageCode));
        setBusy(true);
        setError(null);
        try {
            const next = await advanceEngagementAction(card.engagementId);
            setSelectedStage(next.stageCode);
            await load(trajectoryCode ?? undefined);
        }
        catch (err) {
            setColumns(columnsSnapshotRef.current);
            setError(mapActionError(err, 'agent'));
        }
        finally {
            setBusy(false);
        }
    }
    function openCard(card) {
        if (isComputed) {
            navigate(`/agents/${card.agentId}`);
            return;
        }
        setSelected(card);
        setSelectedStage(card.stageCode);
        setSheet('card');
    }
    if (loading && trajectories.length === 0 && columns.length === 0) {
        return (_jsxs("div", { className: "space-y-5", children: [_jsxs("header", { className: "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between", children: [_jsxs("div", { className: "space-y-1", children: [_jsx("h1", { className: "text-[length:var(--unity-font-size-title)] font-semibold text-foreground", children: "\u0412\u043E\u0440\u043E\u043D\u043A\u0438" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "\u0412\u043E\u0440\u043E\u043D\u043A\u0430 \u2014 \u044D\u0442\u043E \u043F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u0430. \u0420\u0443\u0447\u043D\u044B\u0435 \u043F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u044B \u0437\u0430\u043F\u043E\u043B\u043D\u044F\u0435\u0442 \u0411\u041C; \u0432\u044B\u0447\u0438\u0441\u043B\u044F\u0435\u043C\u044B\u0435 (\u0411\u0422-\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F, \u0414\u043E\u0433\u043E\u0432\u043E\u0440 \u0441 \u0410\u041D) \u0441\u0442\u0440\u043E\u044F\u0442\u0441\u044F \u0438\u0437 CRM." })] }), _jsx(AgentScopeToggle, {})] }), _jsx(Skeleton, { className: "h-12 w-full" }), _jsx("div", { className: "flex gap-3 overflow-hidden", children: Array.from({ length: 4 }).map((_, index) => (_jsx(Skeleton, { className: "h-80 min-w-[220px] flex-1" }, index))) })] }));
    }
    const sheetTitle = sheet === 'create-agent'
        ? 'Добавить агента'
        : (selected?.agentDisplayName ?? 'Агент');
    return (_jsxs("div", { className: "space-y-5", children: [_jsxs("header", { className: "flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between", children: [_jsxs("div", { className: "space-y-1", children: [_jsx("h1", { className: "text-[length:var(--unity-font-size-title)] font-semibold text-foreground", children: "\u0421\u0442\u0430\u0440\u044B\u0435 \u043F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u044B" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "\u0422\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u0438\u0439 \u043A\u0430\u0442\u0430\u043B\u043E\u0433 \u043F\u043E \u0430\u0433\u0435\u043D\u0442\u0443. \u041E\u0441\u043D\u043E\u0432\u043D\u043E\u0439 \u043F\u0443\u0442\u044C \u0431\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440\u0430 \u2014 \u0434\u043E\u0441\u043A\u0438 \u0446\u0438\u043A\u043B\u0430 \u0438 \u043C\u0430\u0442\u0440\u0438\u0446\u0430 \u043F\u0440\u043E\u0435\u043A\u0442\u043E\u0432." }), _jsxs("p", { className: "text-sm", children: [_jsx(Link, { className: "text-primary hover:underline", to: "/engagements?view=bt", children: "\u041A \u0434\u043E\u0441\u043A\u0430\u043C \u0446\u0438\u043A\u043B\u0430" }), ' · ', _jsx(Link, { className: "text-primary hover:underline", to: "/engagements/projects", children: "\u041A \u043C\u0430\u0442\u0440\u0438\u0446\u0435 \u043F\u0440\u043E\u0435\u043A\u0442\u043E\u0432" })] })] }), _jsx(AgentScopeToggle, {})] }), _jsxs(Alert, { children: [_jsx(AlertTitle, { children: "\u041D\u0435 \u0446\u0435\u043B\u0435\u0432\u0430\u044F \u043C\u043E\u0434\u0435\u043B\u044C" }), _jsxs(AlertDescription, { children: ["\u041F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u0430 \u00AB\u0410\u0432\u0442\u043E\u0431\u0443\u0441\u043D\u044B\u0435 \u0442\u0443\u0440\u044B\u00BB (0\u20134) \u0437\u0434\u0435\u0441\u044C \u0442\u043E\u043B\u044C\u043A\u043E \u043F\u043E \u0442\u0435\u0445\u043D\u0438\u0447\u0435\u0441\u043A\u043E\u043C\u0443 URL", _jsx("code", { className: "mx-1", children: "?trajectory=" }), ". \u0412 \u043C\u0435\u043D\u044E \u0446\u0438\u043A\u043B\u0430 \u0435\u0451 \u043D\u0435\u0442."] })] }), trajectories.length > 0 ? (_jsx("nav", { "aria-label": "\u041F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u044B", className: "flex flex-wrap gap-2", children: trajectories.map((item) => {
                    const active = item.code === trajectoryCode;
                    return (_jsx(Button, { type: "button", variant: active ? 'default' : 'outline', onClick: () => void load(item.code), children: item.title }, item.code));
                }) })) : null, error && sheet === null ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(trajectoryCode ?? undefined), children: uiCopy.retry })] })) : null, trajectories.length === 0 ? (_jsxs(Alert, { children: [_jsx(AlertTitle, { children: "\u041F\u0440\u043E\u0433\u0440\u0430\u043C\u043C \u043D\u0435\u0442 \u0432 \u0441\u043F\u0440\u0430\u0432\u043E\u0447\u043D\u0438\u043A\u0435" }), _jsx(AlertDescription, { children: "\u0414\u043E\u0441\u0442\u0443\u043F\u043D\u044B \u0442\u043E\u043B\u044C\u043A\u043E \u00AB\u0410\u0432\u0442\u043E\u0431\u0443\u0441\u043D\u044B\u0435 \u0442\u0443\u0440\u044B\u00BB, \u00AB\u0411\u0422-\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F\u00BB \u0438 \u00AB\u0414\u043E\u0433\u043E\u0432\u043E\u0440 \u0441 \u0410\u041D\u00BB. \u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 seed \u0438\u043B\u0438 \u043E\u0431\u0440\u0430\u0442\u0438\u0442\u0435\u0441\u044C \u043A \u0430\u0434\u043C\u0438\u043D\u0438\u0441\u0442\u0440\u0430\u0442\u043E\u0440\u0443." })] })) : (_jsxs("section", { className: "w-full space-y-4", children: [_jsx(StageToolbar, { programTitle: title, columns: columns, busy: busy, editing: editingStages, onEditingChange: setEditingStages, onAddStage: isComputed
                            ? undefined
                            : async (stageTitle) => {
                                setBusy(true);
                                setError(null);
                                try {
                                    await persistStages([
                                        ...columns.map((column) => ({
                                            code: column.stageCode,
                                            title: column.title,
                                        })),
                                        { title: stageTitle },
                                    ]);
                                }
                                catch (err) {
                                    setError(mapActionError(err, 'trajectory'));
                                    throw err;
                                }
                                finally {
                                    setBusy(false);
                                }
                            }, onReorder: isComputed
                            ? undefined
                            : async (stages) => {
                                setBusy(true);
                                setError(null);
                                try {
                                    await persistStages(stages);
                                }
                                catch (err) {
                                    setError(mapActionError(err, 'trajectory'));
                                    throw err;
                                }
                                finally {
                                    setBusy(false);
                                }
                            } }), _jsxs("div", { className: "flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between", children: [_jsxs("div", { className: "space-y-1", children: [_jsx("label", { className: "text-sm font-medium", htmlFor: "engagement-board-search", children: uiCopy.engagementBoardSearchLabel }), _jsx("input", { id: "engagement-board-search", type: "search", value: boardQuery, onChange: (event) => setBoardQuery(event.target.value), placeholder: uiCopy.engagementTableSearch, className: "flex h-11 w-full max-w-md rounded-md border border-input bg-background px-3 text-sm" })] }), !isComputed ? (_jsx(Button, { type: "button", variant: "secondary", onClick: () => void openCreateAgent(), children: "\u0414\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0430\u0433\u0435\u043D\u0442\u0430" })) : null] }), columns.length === 0 ? (_jsxs(Alert, { children: [_jsx(AlertTitle, { children: "\u042D\u0442\u0430\u043F\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" }), _jsx(AlertDescription, { children: "\u041D\u0430\u0436\u043C\u0438\u0442\u0435 \u00AB+\u00BB \u0438 \u0432\u0432\u0435\u0434\u0438\u0442\u0435 \u043D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u044D\u0442\u0430\u043F\u0430." })] })) : (_jsx(EngagementKanban, { columns: columns, metrics: metrics, placementMode: placementMode, query: boardQuery, busy: busy, editingStages: editingStages, onOpenCard: openCard, onAdvanceDrop: handleAdvanceDrop, onMoveStage: isComputed ? undefined : moveStage }))] })), _jsxs(Sheet, { open: sheet !== null, onClose: () => {
                    setSheet(null);
                    setSelected(null);
                }, title: sheetTitle, children: [error && sheet !== null ? (_jsxs(Alert, { variant: "destructive", className: "mb-4", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error })] })) : null, sheet === 'create-agent' ? (agentsLoading ? (_jsxs("div", { className: "flex flex-col gap-2", "aria-busy": "true", children: [_jsx(Skeleton, { className: "h-10 w-full" }), _jsx(Skeleton, { className: "h-10 w-full" }), _jsx(Skeleton, { className: "h-10 w-full max-w-sm" }), _jsx(Skeleton, { className: "h-24 w-full" })] })) : (_jsx(CreateEngagementForm, { agents: agents, busyAgentIds: busyAgentIds, columns: columns, defaultStageCode: selectedStage ?? columns[0]?.stageCode ?? '', busy: busy, onSubmit: (input) => runAction(async () => {
                            if (!trajectoryCode) {
                                return;
                            }
                            await createEngagementAction({
                                agentId: input.agentId,
                                trajectoryCode,
                                initialStageCode: input.initialStageCode,
                            });
                            setSelectedStage(input.initialStageCode);
                            await load(trajectoryCode);
                        }) }))) : null, sheet === 'card' && selected && !isComputed ? (_jsxs("div", { className: "space-y-4", children: [_jsxs("p", { className: "text-sm", children: ["\u0421\u0435\u0439\u0447\u0430\u0441: ", selectedColumn?.title ?? selected.stageCode] }), _jsx("p", { className: "text-sm text-muted-foreground", children: selected.visitedProjectCount > 0
                                    ? `Уже посетил проектов: ${selected.visitedProjectCount}`
                                    : 'Ещё не посещал проекты в этой программе.' }), detail && detail.transitions.length > 0 ? (_jsx("ol", { className: "space-y-1 text-sm text-muted-foreground", children: detail.transitions.map((item, index) => {
                                    const stepTitle = columns.find((column) => column.stageCode === item.toStageCode)?.title ?? item.toStageCode;
                                    return (_jsxs("li", { children: [new Date(item.at).toLocaleString('ru-RU'), " \u00B7 ", stepTitle] }, `${item.at}-${index}`));
                                }) })) : null, _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(Button, { type: "button", disabled: busy || selected.status !== 'ACTIVE', onClick: () => void runAction(async () => {
                                            const next = await advanceEngagementAction(selected.engagementId);
                                            setSelectedStage(next.stageCode);
                                            await load(trajectoryCode ?? undefined);
                                        }), children: "\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0439 \u0448\u0430\u0433" }), _jsx(Button, { type: "button", variant: "destructive", disabled: busy || selected.status !== 'ACTIVE', onClick: () => void runAction(async () => {
                                            await cancelEngagementAction(selected.engagementId);
                                            await load(trajectoryCode ?? undefined);
                                        }), children: "\u0421\u043D\u044F\u0442\u044C \u0441 \u043F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u044B" })] })] })) : null] })] }));
}
